// The fetched groupings in words, and which of them a layout needs fetched.
// The server sends keys only ("2026-10-12", "1", "picked"); the reader's
// language is the screen's business.

import { monthName, weekdayName } from '../lib/i18n/format'
import { SPOKEN, type StaffKey, type StaffLanguage } from '../lib/staffText'
import { isFetched, type BreakDim, type FetchedDim, type Layout } from './insightCalc'

// The desk screen's own words for where a ticket came from: one name per thing.
// Except the desk: beside the Desk field, a channel row called "Desk" read as a desk.
const CHANNEL: Record<string, StaffKey> = {
  kiosk: 'counter.source.kiosk',
  reception: 'counter.source.reception',
  counter: 'insight.channel.counter',
  mobile: 'counter.source.mobile',
  appointment: 'counter.source.appointment',
}
const PRIORITY: Record<string, StaffKey> = {
  standard: 'insight.priority.standard',
  priority: 'common.priority',
  vip: 'common.vip',
}
const CALL: Record<string, StaffKey> = {
  next: 'insight.call.next',
  picked: 'insight.call.picked',
  auto: 'insight.call.auto',
  walkin: 'counter.serveWalkIn',
  none: 'insight.call.none',
}

/** A date-only key at UTC midnight, formatted in UTC, so no browser zone moves it a day. */
function utc(key: string): Date | null {
  if (!/^\d{4}-\d{2}(-\d{2})?$/.test(key)) return null
  const d = new Date((key.length === 7 ? key + '-01' : key) + 'T00:00:00Z')
  return Number.isNaN(d.getTime()) ? null : d
}
const DAY = 86_400_000

/** The first letter up, Azerbaijani's dotted capital İ included, without the runtime's locale data. */
const capital = (x: string, lang: StaffLanguage) => (lang === 'az' && x.startsWith('i') ? 'İ' : x.charAt(0).toUpperCase()) + x.slice(1)

/** The period a label is read in; a week or month cut by its edge says so. */
export type Span = { from: string; to: string }

/** The dates of [first, last] that the span covers, or null when it covers them all. */
function clamp(first: Date, last: Date, span: Span | undefined): [Date, Date] | null {
  const from = span && utc(span.from)
  const to = span && utc(span.to)
  const a = from && from > first ? from : first
  const b = to && to < last ? to : last
  return a.getTime() === first.getTime() && b.getTime() === last.getTime() ? null : [a, b]
}

// Names from Intl in the reader's language, tables where the runtime lacks it (i18n/format.ts).
const monthOf = (d: Date, lang: StaffLanguage, form: 'short' | 'long') => monthName(lang, d.getUTCMonth(), form)

/** "12–18 Oct", "28 Sep – 4 Oct", "14 Oct". */
function range(a: Date, b: Date, lang: StaffLanguage, form: 'short' | 'long'): string {
  const [d1, m1, d2, m2] = [a.getUTCDate(), monthOf(a, lang, form), b.getUTCDate(), monthOf(b, lang, form)]
  if (a.getTime() === b.getTime()) return `${d2} ${m2}`
  return m1 === m2 ? `${d1}–${d2} ${m2}` : `${d1} ${m1} – ${d2} ${m2}`
}

function week(monday: Date, lang: StaffLanguage, span: Span | undefined): string {
  const sunday = new Date(monday.getTime() + 6 * DAY)
  const [a, b] = clamp(monday, sunday, span) ?? [monday, sunday]
  return range(a, b, lang, 'short')
}

function month(first: Date, lang: StaffLanguage, span: Span | undefined): string {
  const last = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0))
  const cut = clamp(first, last, span)
  const year = String(first.getUTCFullYear())
  if (!cut) return `${capital(monthName(lang, first.getUTCMonth(), 'alone'), lang)} ${year}`
  return `${range(cut[0], cut[1], lang, 'long')} ${year}`
}

/** The whole day name, capitalised: Monday, Bazar ertəsi, Понедельник. */
const weekday = (d: Date, lang: StaffLanguage) => capital(weekdayName(lang, d.getUTCDay()), lang)

function language(code: string, lang: StaffLanguage): string {
  return SPOKEN[code.toLowerCase()]?.[lang] ?? code.toUpperCase()
}

/** "03:30" read as the interval it starts: "03:30–03:45". */
function interval(start: string, minutes: number): string {
  const m = /^(\d{2}):(\d{2})$/.exec(start)
  if (!m) return start
  const end = (Number(m[1]) * 60 + Number(m[2]) + minutes) % (24 * 60)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${start}–${pad(Math.floor(end / 60))}:${pad(end % 60)}`
}

/** A member of a fetched grouping as the reader says it; the key itself when unknown.
 *  With the span read, a week or month the span cuts names only the dates it covers. */
export function memberLabel(dim: BreakDim, key: string, lang: StaffLanguage, say: (k: StaffKey) => string, span?: Span): string {
  const named = (table: Record<string, StaffKey>) => {
    const k = table[key]
    return k ? say(k) : key
  }
  switch (dim) {
    case 'day': {
      const d = utc(key)
      if (!d) return key
      return `${weekday(d, lang)} ${range(d, d, lang, 'short')}`
    }
    case 'week': {
      const d = utc(key)
      return d ? week(d, lang, span) : key
    }
    case 'month': {
      const d = utc(key)
      return d ? month(d, lang, span) : key
    }
    case 'interval15':
      return interval(key, 15)
    case 'interval30':
      return interval(key, 30)
    case 'weekday': {
      const n = Number(key)
      // 12 Oct 2026 is a Monday: ISO day n is 11 + n October.
      return Number.isInteger(n) && n >= 1 && n <= 7 ? weekday(new Date(Date.UTC(2026, 9, 11 + n)), lang) : key
    }
    case 'channel':
      return named(CHANNEL)
    case 'priority':
      return named(PRIORITY)
    case 'call':
      return named(CALL)
    case 'language':
      return key === 'none' ? say('insight.lang.none') : language(key, lang)
    default:
      return key
  }
}

/** The fetched groupings a layout uses, in the order placed: two at most. */
export function dimNeeds(layout: Layout): FetchedDim[] {
  return [...layout.rows, ...layout.cols].filter(isFetched)
}

/** Where a fetched grouping is kept: per summary read (gen), branch, period, grouping and bands.
 *  A re-read summary is a new gen, so its rows are always read with it. */
export const fieldKey = (gen: number, branchId: string, from: string, to: string, by: FetchedDim | `cross:${string}`, bands: string) =>
  [gen, branchId, from, to, by, bands].join('|')

/** Forget every grouping kept for an older summary read. */
export function keepOnly(kept: Map<string, unknown>, gen: number) {
  for (const k of [...kept.keys()]) if (!k.startsWith(`${gen}|`)) kept.delete(k)
}
